import { EmailTemplate, EmailTemplateCategory } from '@spike/email-marketing-model';
import { fetchEmailTemplatesThunk } from '@spike/email-marketing-action';
import {
    FullWindowModal,
    FullWindowModalBody,
    FullWindowModalCloseButton,
    FullWindowModalHeader
} from 'components/UI/V3/Modals/FullWindowModal';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import EmailTemplatesGalleryHeader from './Header/EmailTemplatesHeader';
import EmailTemplatesGalleryList from './List/EmailTemplatesList';
import styles from './EmailTemplatesGallery.module.scss';

type TemplateFilterId = 'all' | EmailTemplateCategory;

export interface TemplateFilterOption {
    id: TemplateFilterId;
    name: string;
}

interface TemplatesByCategory {
    category: EmailTemplateCategory | 'all';
    templates: EmailTemplate[];
}

const templateFilterOptions: TemplateFilterOption[] = [
    { id: 'all', name: 'All Templates' },
    { id: 'engagement', name: 'Engagement' },
    { id: 'promotional', name: 'Promotional' },
    { id: 'seasonal', name: 'Seasonal' }
];

// `system_default` is intentionally omitted — the gallery hides that group so
// the "All" view lines up with what the filter tabs (Engagement / Promotional /
// Seasonal) can reach. Templates flagged with that category are dropped in
// `systemTemplates` below so nothing silently leaks into the render.
const templateCategories: EmailTemplateCategory[] = ['engagement', 'promotional', 'seasonal'];

/**
 * Two operating modes:
 *
 *   Standalone (default): full-screen overlay with logo + title + X close.
 *     Used from the Templates tab via "Start With a Template". `onClose`
 *     dismisses the overlay.
 *
 *   Picker (when `pickerMode` is true): embedded inside another container
 *     (typically the campaign wizard step 2 right column). Top bar is
 *     omitted, layout is non-fixed. `onSelectTemplate` is invoked when the
 *     user clicks "Use Template" on any card. `onClose` is not used.
 */
interface EmailTemplatesGalleryProps {
    onClose?: () => void;
    pickerMode?: boolean;
    onSelectTemplate?: (template: EmailTemplate) => void;
    /**
     * Required — opens the template preview modal for the chosen card. The
     * parent owns the modal's visibility state because the consumer
     * controls what "Use Template" does from inside the modal (clone+navigate
     * in standalone, set emailTemplateId in the wizard).
     */
    onPreviewTemplate: (template: EmailTemplate) => void;
}

const EmailTemplatesGallery: FunctionComponent<EmailTemplatesGalleryProps> = ({
    onClose,
    pickerMode = false,
    onSelectTemplate,
    onPreviewTemplate
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const templates = useSelector((state: RootState) => state.emailTemplates.list);
    const error = useSelector((state: RootState) => state.emailTemplates.error);

    const [selectedTemplateFilter, setSelectedTemplateFilter] = useState<TemplateFilterId>('all');
    // Gates the empty/list flash on first mount — the slice's `loading` flag
    // is false until the dispatch reaches the reducer one render later, so
    // the gallery list would otherwise render empty for one frame.
    const [initialFetchInFlight, setInitialFetchInFlight] = useState<boolean>(true);

    useEffect(() => {
        setInitialFetchInFlight(true);
        (dispatch(fetchEmailTemplatesThunk(apiClientWrapper)) as unknown as Promise<unknown>).finally(() =>
            setInitialFetchInFlight(false)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const systemTemplates = useMemo(
        () => templates.filter(template => template.system === true && template.category !== 'system_default'),
        [templates]
    );

    const filteredTemplates = useMemo(() => {
        if (selectedTemplateFilter === 'all') {
            return systemTemplates;
        }
        return systemTemplates.filter(template => template.category === selectedTemplateFilter);
    }, [selectedTemplateFilter, systemTemplates]);

    const templatesByCategory = useMemo<TemplatesByCategory[]>(() => {
        // Backend `category` field is pending (gap #2 in INTEGRATION_PLAN).
        // Until BE ships it, group every template under a single "All Templates"
        // section so the gallery still renders the seeded templates end-to-end.
        const haveCategoryData = filteredTemplates.some(template => Boolean(template.category));

        if (!haveCategoryData) {
            return filteredTemplates.length > 0 ? [{ category: 'all', templates: filteredTemplates }] : [];
        }

        if (selectedTemplateFilter !== 'all') {
            return [{ category: selectedTemplateFilter, templates: filteredTemplates }];
        }

        return templateCategories.map(category => ({
            category,
            templates: filteredTemplates.filter(template => template.category === category)
        }));
    }, [filteredTemplates, selectedTemplateFilter]);

    const handleSelectTemplateFilter = (filterId: string) => {
        setSelectedTemplateFilter(filterId as TemplateFilterId);
    };

    const filterRow = (
        <EmailTemplatesGalleryHeader
            options={templateFilterOptions}
            selectedFilter={selectedTemplateFilter}
            onChange={handleSelectTemplateFilter}
        />
    );

    const content = (
        <div className={styles.templateGalleryContent}>
            {initialFetchInFlight ? (
                <div className={styles.loaderContainer}>
                    <Spinner />
                </div>
            ) : (
                <>
                    {error && <div>Failed to load templates. Please try again.</div>}
                    {!error && templates.length === 0 && <div>No templates available.</div>}
                    <EmailTemplatesGalleryList
                        templatesByCategory={templatesByCategory}
                        onSelectTemplate={onSelectTemplate}
                        onPreviewTemplate={onPreviewTemplate}
                    />
                </>
            )}
        </div>
    );

    // Picker mode — embedded inside another container (e.g. the campaign
    // wizard's right column). Chrome is the parent's job; we render just the
    // filter row + cards in a flex flow.
    if (pickerMode) {
        return (
            <div className={styles.templateGalleryPicker}>
                {filterRow}
                {content}
            </div>
        );
    }

    // Standalone — full-window takeover using the V3 `FullWindowModal`
    // primitive. The filter row sits between the header slot and the body
    // (as a direct sibling of both) so it stays visible while the cards
    // scroll inside the body.
    return (
        <FullWindowModal>
            <FullWindowModalHeader>
                <div className={styles.standaloneTopBar}>
                    <img
                        className={styles.logo}
                        src="/images/logo/cuddles-logo.svg"
                        alt="Cuddles"
                    />
                    <h1 className={styles.title}>Template Gallery</h1>
                    {onClose ? (
                        <FullWindowModalCloseButton
                            onClick={onClose}
                            ariaLabel="Close gallery"
                            className={styles.closeButton}
                        />
                    ) : (
                        <span aria-hidden="true" />
                    )}
                </div>
            </FullWindowModalHeader>

            {filterRow}

            <FullWindowModalBody>{content}</FullWindowModalBody>
        </FullWindowModal>
    );
};

export default EmailTemplatesGallery;
